/*
  The mobile mega menu drawer.

  The theme's own hamburger opens it: the click is caught before the theme sees it, so the
  header markup is untouched and removing this section hands the button straight back to the
  theme's drawer.

  A category slides its panel in over the list, the back button slides it away, and the headings
  inside open and close one at a time.
*/
if (!customElements.get('mega-menu-drawer')) {
  customElements.define(
    'mega-menu-drawer',
    class MegaMenuDrawer extends HTMLElement {
      constructor() {
        super();

        this.panel = this.querySelector('[data-mmd-panel]');
        this.main = this.querySelector('[data-mmd-main]');
        this.dim = this.querySelector('[data-mmd-dim]');
        this.closeButton = this.querySelector('[data-mmd-close]');
        this.currencySlot = this.querySelector('[data-mmd-currency]');
        this.trigger = null;

        this.onHamburgerClick = this.onHamburgerClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
      }

      connectedCallback() {
        this.onDisable = this.teardown.bind(this);
        this.onEnable = this.init.bind(this);
        document.addEventListener('mega-menu:disabled', this.onDisable);
        document.addEventListener('mega-menu:enabled', this.onEnable);

        // The A/B test can have turned the menu off already; the switch in the section owns it.
        if (document.documentElement.dataset.megaMenu !== 'off') this.init();
      }

      /*
        The hamburger listener is the reason this has to come apart cleanly. It sits on the
        document and swallows the click before the theme sees it, so leaving it bound for the
        control group would give them a button that opens nothing at all.
      */
      init() {
        if (this.live) return;
        this.live = true;

        /*
          Capture phase on the document, so this runs before the listeners the theme binds to
          the summary itself; stopping propagation there leaves the theme's drawer closed, and
          preventDefault stops the <details> from toggling.
        */
        document.addEventListener('click', this.onHamburgerClick, true);
        document.addEventListener('keydown', this.onKeydown);

        if (this.bound) return;
        this.bound = true;

        if (this.closeButton) this.closeButton.addEventListener('click', () => this.close());
        if (this.dim) this.dim.addEventListener('click', () => this.close());

        this.querySelectorAll('[data-mmd-open]').forEach((button) => {
          button.addEventListener('click', () => this.openSub(button));
        });

        this.querySelectorAll('[data-mmd-back]').forEach((button) => {
          button.addEventListener('click', () => this.closeSub());
        });

        this.querySelectorAll('.mmd__acc-btn[aria-controls]').forEach((button) => {
          button.addEventListener('click', () => this.toggleSection(button));
        });
      }

      teardown() {
        if (!this.live) return;
        this.live = false;

        this.close();
        document.removeEventListener('click', this.onHamburgerClick, true);
        document.removeEventListener('keydown', this.onKeydown);
        document.body.classList.remove('mmd-open');
      }

      disconnectedCallback() {
        this.teardown();
        document.removeEventListener('mega-menu:disabled', this.onDisable);
        document.removeEventListener('mega-menu:enabled', this.onEnable);
      }

      onHamburgerClick(event) {
        const summary = event.target.closest('.header__icon--menu');
        if (!summary || this.contains(summary)) return;
        // Above the breakpoint the drawer is not on screen and the bar is in charge.
        if (!this.offsetParent && getComputedStyle(this).display === 'none') return;

        event.preventDefault();
        event.stopPropagation();

        this.trigger = summary;
        this.classList.contains('is-open') ? this.close() : this.open();
      }

      open() {
        this.classList.add('is-open');
        this.panel.setAttribute('aria-hidden', 'false');
        document.body.classList.add('mmd-open');
        this.adoptCurrencySwitcher();
        if (this.closeButton) setTimeout(() => this.closeButton.focus(), 50);
      }

      close() {
        if (!this.classList.contains('is-open')) return;

        this.classList.remove('is-open');
        this.panel.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('mmd-open');

        // Reset to the category list once it has slid out of sight, not while it is moving.
        setTimeout(() => this.closeSub(true), 300);

        if (this.trigger) this.trigger.focus();
      }

      openSub(button) {
        const sub = this.querySelector('#' + button.getAttribute('aria-controls'));
        if (!sub) return;

        sub.scrollTop = 0;
        sub.classList.add('is-open');
        this.main.classList.add('is-pushed');
        button.setAttribute('aria-expanded', 'true');

        const back = sub.querySelector('[data-mmd-back]');
        if (back) setTimeout(() => back.focus(), 60);
      }

      closeSub(silent) {
        const open = this.querySelector('.mmd__sub.is-open');
        this.querySelectorAll('.mmd__sub').forEach((sub) => sub.classList.remove('is-open'));
        this.main.classList.remove('is-pushed');

        this.querySelectorAll('[data-mmd-open]').forEach((button) => {
          button.setAttribute('aria-expanded', 'false');
        });

        if (silent || !open) return;

        const button = this.querySelector('[data-mmd-open="' + open.id + '"]');
        if (button) button.focus();
      }

      toggleSection(button) {
        const panel = this.querySelector('#' + button.getAttribute('aria-controls'));
        if (!panel) return;

        const open = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', open ? 'false' : 'true');
        panel.hidden = open;
      }

      /*
        The theme keeps a single currency switcher and moves it into whichever drawer is on
        screen. Taking that element would break the theme's own move on the next resize, so a
        copy is used: every handler behind it is bound to the document and works on all copies.
      */
      adoptCurrencySwitcher() {
        if (!this.currencySlot || this.currencySlot.childElementCount > 0) return;

        const original = document.querySelector('#currency-switch');
        if (!original) return;

        this.currencySlot.appendChild(original.cloneNode(true));
      }

      onKeydown(event) {
        if (event.key !== 'Escape' || !this.classList.contains('is-open')) return;

        if (this.querySelector('.mmd__sub.is-open')) {
          this.closeSub();
          return;
        }

        this.close();
      }
    }
  );
}
